import api from '../api';

// Cloudflare Images / Stream delivery hosts (same accounts the gallery uses).
export const imageDelivery = "https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ";
export const streamSubdomain = "customer-uqadfqxwoqnv17yr.cloudflarestream.com";

/**
 * POST a file to a one-time Cloudflare URL with real progress.
 *
 * `fetch` cannot report upload progress at all, so a 400 MB video looked
 * identical to a stalled one — the whole reason the old uploader could only
 * show a spinner. XHR still exposes `upload.onprogress`, so it is the right
 * tool here even though the rest of the app is on axios/fetch.
 */
function postFile(url, file, { onProgress, signal } = {}) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const form = new FormData();
        form.append('file', file);

        xhr.open('POST', url);

        if (onProgress) {
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
                }
            };
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                if (onProgress) onProgress(100);
                resolve(xhr.responseText);
            } else {
                reject(new Error(`upload_failed_${xhr.status}`));
            }
        };
        xhr.onerror = () => reject(new Error('upload_network_error'));
        xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));

        if (signal) {
            if (signal.aborted) { xhr.abort(); return; }
            signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }

        xhr.send(form);
    });
}

/**
 * Two-step direct upload: ask the backend for a one-time Cloudflare URL,
 * POST the file straight to Cloudflare, then keep the delivery URL.
 * Returns the public URL, or null when the handshake fails.
 *
 * `onProgress` is called with 0–100 while the bytes are in flight.
 */
export async function uploadImage(file, { onProgress, signal } = {}) {
    const res = await api.get('api/requesturlimage/');
    if (res.status !== 200 || !res.data?.upload_url) return null;

    await postFile(res.data.upload_url, file, { onProgress, signal });

    return `${imageDelivery}/${res.data.image_id}/public`;
}

export async function uploadVideo(file, { onProgress, signal } = {}) {
    const res = await api.get('api/requesturlvideo/');
    if (res.status !== 200) return null;

    // The Stream endpoint answers with entry pairs rather than an object.
    const data = res.data;
    const urlEntry = data.find(([key]) => key === 'uploadURL' || key === 'upload_url');
    const uidEntry = data.find(([key]) => key === 'uid');
    if (!urlEntry || !uidEntry) return null;

    await postFile(urlEntry[1], file, { onProgress, signal });

    return `${streamSubdomain}/${uidEntry[1]}`;
}

/** A still from a Stream video. `url` is stored without its scheme. */
export function streamThumb(url, { time = '2s', height, width, fit = 'crop' } = {}) {
    const params = new URLSearchParams({ time });
    if (height) params.set('height', height);
    if (width) params.set('width', width);
    if (height || width) params.set('fit', fit);
    return `https://${url}/thumbnails/thumbnail.jpg?${params.toString()}`;
}

/**
 * The animated strip Stream renders on demand — what makes a tile preview on
 * hover the way a video tile is expected to. It is a separate render on
 * Cloudflare's side, so it is only ever requested on intent (hover/focus),
 * never eagerly for a whole grid.
 */
export function streamPreview(url, { duration = 4, height = 320, fps = 8 } = {}) {
    const params = new URLSearchParams({
        time: '1s',
        duration: String(duration),
        height: String(height),
        fps: String(fps),
    });
    return `https://${url}/thumbnails/thumbnail.gif?${params.toString()}`;
}

/**
 * A freshly uploaded video is not watchable the moment the POST returns —
 * Cloudflare still has to encode it, and until it does every thumbnail 404s.
 * Polling the thumbnail is how we tell "processing" from "ready", so the
 * uploader can say which one it is instead of publishing a broken tile.
 *
 * Resolves true when ready, false when the wait ran out (the item is still
 * saved either way — it will simply finish encoding on its own).
 */
export function waitForStreamReady(url, { attempts = 40, interval = 3000, signal } = {}) {
    return new Promise((resolve) => {
        let tries = 0;
        let timer = null;

        const stop = (value) => {
            if (timer) clearTimeout(timer);
            resolve(value);
        };

        if (signal) signal.addEventListener('abort', () => stop(false), { once: true });

        const probe = () => {
            if (signal?.aborted) return;
            tries += 1;
            const img = new Image();
            // Cache-bust: a 404 answer is cacheable and would pin us to "not ready".
            img.src = `${streamThumb(url, { time: '1s', height: 80 })}&_=${tries}`;
            img.onload = () => stop(true);
            img.onerror = () => {
                if (tries >= attempts) return stop(false);
                timer = setTimeout(probe, interval);
            };
        };

        probe();
    });
}
